import type { StoreCartShippingOption } from "@medusajs/types";
import { useMemo } from "react";
import { useCartShippingOptions } from "./queries";

export interface FreeShippingIncentive {
  /** The lowest qualifying threshold across the cart's options. */
  threshold: number;
  /**
   * What the qualifying option costs below the threshold - the amount the
   * buyer stops paying once the basket clears it. Undefined when that option
   * has no unconditional (rule-less) paid price to compare against, in which
   * case copy should fall back to the plain "free shipping" phrasing rather
   * than invent a figure.
   *
   * Deliberately NOT the currently-selected method's price: the threshold
   * zeroes the qualifying option (typically standard), so quoting an Express
   * selection's price here would promise a saving the threshold doesn't give.
   */
  savings?: number;
  /**
   * The name of the option the threshold belongs to - typically the standard
   * service. Free shipping is a property of *one* option, not of the cart, so
   * copy that says "free shipping" unqualified over-promises to a buyer who
   * has Express selected: crossing the threshold zeroes standard and leaves
   * their Express charge untouched. Naming the option keeps the promise the
   * same size as the offer. Undefined when the option carries no name, in
   * which case copy falls back to the unqualified wording.
   */
  optionName?: string;
}

/**
 * Derive the free-shipping incentive from a cart's shipping options.
 *
 * A shipping option qualifies for free shipping when it has a price of `0`
 * gated behind an `item_total >= X` price rule. When several such options
 * exist, the lowest threshold wins (that is the first one the basket clears).
 * The savings figure comes from the same option that supplied the winning
 * threshold (see {@link FreeShippingIncentive.savings}).
 *
 * Amounts are in the store currency's major unit (the same unit as
 * `cart.totals.item_subtotal`), so no cents conversion is applied.
 *
 * @returns the incentive, or `undefined` when the cart has no free-shipping
 *   option configured.
 */
export function deriveFreeShippingIncentive(
  options: StoreCartShippingOption[]
): FreeShippingIncentive | undefined {
  let best: FreeShippingIncentive | undefined;

  for (const shippingOption of options) {
    const prices = shippingOption.prices ?? [];

    for (const p of prices) {
      if (p.amount !== 0 || !p.price_rules?.length) continue;

      const minAmountRule = p.price_rules.find(
        (pr) => pr.attribute === "item_total" && pr.operator === "gte"
      );
      if (!minAmountRule) continue;

      const threshold = +minAmountRule.value;
      // A qualifying threshold is a positive, finite amount - there is no
      // distance to close otherwise, and `getAmountToFreeShipping` would
      // report nothing for it anyway. The guard also keeps a malformed rule
      // value (`+"100 AUD"` -> NaN, `+""` -> 0) from clobbering a valid
      // threshold found on another tier, which it otherwise would: every
      // comparison against NaN is false.
      if (!Number.isFinite(threshold) || threshold <= 0) continue;

      // The paid price the free tier replaces: this option's unconditional
      // (rule-less) non-zero price in the free tier's own currency - the
      // prices array carries every currency variant of the option.
      const base = prices.find(
        (pp) =>
          pp.amount > 0 &&
          !pp.price_rules?.length &&
          pp.currency_code === p.currency_code
      );
      const candidate: FreeShippingIncentive = {
        threshold,
        savings: base?.amount,
        optionName: shippingOption.name?.trim() || undefined
      };

      // Lowest threshold wins. On a tie, prefer the candidate that can name
      // its savings, and among named savings the smallest - the prompt must
      // never promise more than crossing the threshold actually saves.
      const beatsBest =
        !best ||
        candidate.threshold < best.threshold ||
        (candidate.threshold === best.threshold &&
          candidate.savings !== undefined &&
          (best.savings === undefined || candidate.savings < best.savings));
      if (beatsBest) {
        best = candidate;
      }
    }
  }

  return best;
}

/**
 * The free-shipping threshold alone - see {@link deriveFreeShippingIncentive}.
 */
export function deriveFreeShippingThreshold(
  options: StoreCartShippingOption[]
): number | undefined {
  return deriveFreeShippingIncentive(options)?.threshold;
}

/**
 * How much more the basket needs to reach the free-shipping threshold.
 *
 * @returns the remaining amount, or `undefined` when there is no threshold or
 *   the basket already qualifies (matches how `FreeShippingPrompt` hides).
 */
export function getAmountToFreeShipping(
  freeShippingThreshold: number | undefined,
  itemSubtotal: number
): number | undefined {
  if (!freeShippingThreshold) return undefined;
  const remaining = freeShippingThreshold - itemSubtotal;
  return remaining > 0 ? remaining : undefined;
}

export interface UseFreeShippingMeterOptions {
  /** The seller-cart to derive the meter for. */
  cartId: string;
  /** The cart's item subtotal (major currency unit). */
  itemSubtotal: number;
  /**
   * The shipping option the cart currently has selected
   * (`cart.shipping_methods[0]?.shipping_option_id`).
   *
   * A threshold zeroes *one* service. When a service is selected, the meter is
   * scoped to that service alone: a buyer on Express is not chasing a
   * threshold that only makes Standard free, so nothing is shown - and if
   * Express carries a threshold of its own, that is the one they see.
   *
   * Omit when the selection isn't known to the caller; the meter then reports
   * the best threshold on offer. A selection the cart's options no longer
   * contain is treated the same way, matching how `ShippingSelector` falls
   * back to its placeholder.
   */
  selectedShippingOptionId?: string | null;
  /** Skip fetching shipping options when false. */
  enabled?: boolean;
}

export interface UseFreeShippingMeterResult {
  /** The free-shipping threshold, or undefined when none is configured. */
  freeShippingThreshold?: number;
  /** Amount still needed to qualify, or undefined when met / no threshold. */
  amountToFreeShipping?: number;
  /**
   * What crossing the threshold saves - the qualifying option's paid base
   * price. Undefined when it cannot be determined; copy then falls back to
   * the plain "free shipping" phrasing.
   */
  freeShippingSavings?: number;
  /**
   * The name of the shipping option the threshold applies to (typically the
   * standard service), so copy can qualify the offer rather than imply every
   * service goes free. Undefined when the option carries no name.
   */
  freeShippingOptionName?: string;
  /** Whether the underlying shipping options are still loading. */
  isLoading: boolean;
}

/**
 * Free-shipping meter data for a single seller-cart, usable in the cart view.
 *
 * This reuses the same threshold source as checkout: it fetches the cart's
 * shipping options (`useCartShippingOptions`) and derives the incentive with
 * {@link deriveFreeShippingIncentive}, then computes the remaining amount with
 * {@link getAmountToFreeShipping}. Nothing here is checkout-specific, so the
 * salon sees distance-to-free-freight while building the basket, not only at
 * checkout.
 */
export function useFreeShippingMeter({
  cartId,
  itemSubtotal,
  selectedShippingOptionId,
  enabled
}: UseFreeShippingMeterOptions): UseFreeShippingMeterResult {
  const { data, isLoading } = useCartShippingOptions(cartId, { enabled });

  const incentive = useMemo(() => {
    const options = data?.shipping_options ?? [];
    // Scope to the selected service, so the meter can only ever promise
    // something the buyer's current choice actually delivers. An unknown or
    // stale selection falls through to the full list rather than blanking the
    // meter - see `selectedShippingOptionId`.
    const selected = selectedShippingOptionId
      ? options.find((option) => option.id === selectedShippingOptionId)
      : undefined;
    return deriveFreeShippingIncentive(selected ? [selected] : options);
  }, [data, selectedShippingOptionId]);
  const freeShippingThreshold = incentive?.threshold;

  const amountToFreeShipping = useMemo(
    () => getAmountToFreeShipping(freeShippingThreshold, itemSubtotal),
    [freeShippingThreshold, itemSubtotal]
  );

  return {
    freeShippingThreshold,
    amountToFreeShipping,
    freeShippingSavings: incentive?.savings,
    freeShippingOptionName: incentive?.optionName,
    isLoading
  };
}
